// ===================================================
// useGoogleCalendar.js — 구글 캘린더 연동 상태 관리 훅
//
// 역할:
//   - 구글 OAuth provider_token 저장/조회
//   - 선택된 캘린더 ID 저장/조회
//   - 캘린더 목록 불러오기
//   - 연동 여부 확인 (isCalendarConnected)
//
// 구글 로그인 시 Supabase가 돌려주는 provider_token을
// Supabase DB(user_settings 테이블)에 저장해 두고,
// 캘린더 API 호출 시마다 꺼내 씁니다.
// ===================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { listCalendars } from '../api/calendarApi';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

// 네이티브 앱 OAuth 콜백 URL (Info.plist URL scheme과 일치해야 함)
const NATIVE_REDIRECT_URL = 'com.omsal.myapp://auth/callback';

// ── 구글 토큰을 Supabase user_settings 테이블에 저장 ─────────
// 테이블 구조: user_id (PK), google_access_token, google_refresh_token,
//             selected_calendar_id, updated_at
async function saveGoogleTokens(accessToken, refreshToken) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from('user_settings').upsert({
    user_id: user.id,
    google_access_token: accessToken,
    google_refresh_token: refreshToken || null,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id' });
}

// ── 저장된 구글 토큰 조회 ─────────────────────────────────────
async function loadGoogleSettings() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from('user_settings')
    .select('google_access_token, google_refresh_token, selected_calendar_id')
    .eq('user_id', user.id)
    .single();

  return data;
}

// ── 선택한 캘린더 ID 저장 ─────────────────────────────────────
async function saveSelectedCalendar(calendarId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from('user_settings').upsert({
    user_id: user.id,
    selected_calendar_id: calendarId,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id' });
}

// ── 구글 토큰 삭제 (연동 해제) ────────────────────────────────
async function clearGoogleTokens() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from('user_settings').upsert({
    user_id: user.id,
    google_access_token: null,
    google_refresh_token: null,
    selected_calendar_id: null,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id' });
}

// ── 메인 훅 ─────────────────────────────────────────────────
export function useGoogleCalendar(user) {
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  // 연동 여부
  const isCalendarConnected = !!googleAccessToken;

  // 앱 시작 시 저장된 토큰 불러오기
  useEffect(() => {
    if (!user) return;
    loadGoogleSettings().then(settings => {
      if (settings?.google_access_token) {
        setGoogleAccessToken(settings.google_access_token);
        setSelectedCalendarId(settings.selected_calendar_id || 'primary');
      }
    });
  }, [user]);

  // googleAccessToken이 설정되면 캘린더 목록 자동 불러오기
  useEffect(() => {
    if (!googleAccessToken) return;
    setCalendarLoading(true);
    listCalendars(googleAccessToken)
      .then(list => setCalendars(list))
      .catch(() => setCalendars([]))
      .finally(() => setCalendarLoading(false));
  }, [googleAccessToken]);

  // 구글 로그인 후 토큰 저장 (useAuth에서 호출)
  const handleGoogleTokens = useCallback(async (accessToken, refreshToken) => {
    setGoogleAccessToken(accessToken);
    await saveGoogleTokens(accessToken, refreshToken);
  }, []);

  // 캘린더 목록 불러오기
  const fetchCalendars = useCallback(async () => {
    if (!googleAccessToken) return;
    setCalendarLoading(true);
    try {
      const list = await listCalendars(googleAccessToken);
      setCalendars(list);
    } catch {
      setCalendars([]);
    } finally {
      setCalendarLoading(false);
    }
  }, [googleAccessToken]);

  // 캘린더 선택 변경
  const handleSelectCalendar = useCallback(async (calendarId) => {
    setSelectedCalendarId(calendarId);
    await saveSelectedCalendar(calendarId);
  }, []);

  // 구글 캘린더 연결 (OAuth 재인증)
  // 네이티브: Browser.open()으로 인앱 브라우저 열기 → useAuth의 appUrlOpen이 처리
  // 웹: 현재 페이지를 OAuth URL로 리다이렉트
  const handleConnectCalendar = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          scopes: 'https://www.googleapis.com/auth/calendar.events',
          redirectTo: NATIVE_REDIRECT_URL,
          skipBrowserRedirect: true,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (!error && data?.url) {
        await Browser.open({ url: data.url, windowName: '_self' });
      }
    } else {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          scopes: 'https://www.googleapis.com/auth/calendar.events',
          redirectTo: window.location.origin,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      // 웹은 리다이렉트되므로 이후 코드 실행되지 않음
    }
  }, []);

  // 연동 해제
  const handleDisconnectCalendar = useCallback(async () => {
    setGoogleAccessToken(null);
    setSelectedCalendarId(null);
    setCalendars([]);
    await clearGoogleTokens();
  }, []);

  return {
    googleAccessToken,
    selectedCalendarId,
    calendars,
    calendarLoading,
    isCalendarConnected,
    handleGoogleTokens,
    fetchCalendars,
    handleSelectCalendar,
    handleConnectCalendar,
    handleDisconnectCalendar,
  };
}
